import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={inputId} className="text-meta text-ink-1 font-medium">
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(
          'h-9 px-3 text-ui bg-paper-0 text-ink-0 border rounded-token-md',
          'placeholder:text-ink-3',
          'focus:outline-none focus-visible:shadow-focus',
          'disabled:bg-paper-1 disabled:text-ink-3 disabled:cursor-not-allowed',
          'transition-colors duration-2 ease-brand',
          error
            ? 'border-status-err focus-visible:border-status-err'
            : 'border-line-2 focus-visible:border-primary',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p id={errorId} className="text-meta text-status-err">
          {error}
        </p>
      ) : null}
    </div>
  );
});
